/**
 * Profile image URL processing utilities for secure proxy architecture.
 * 
 * This module handles the conversion of legacy GCS URLs to secure proxy URLs
 * and provides utilities for processing profile images across the Analytics frontend.
 */

// Stargate service URL for proxy routing
const STARGATE_SERVICE_URL = 'https://stargate-service-202642739529.us-east1.run.app';

// Analytics service URL (for development)
const ANALYTICS_SERVICE_URL = import.meta.env.VITE_ANALYTICS_API_BASE_URL || 'https://analytics-service-202642739529.us-east1.run.app';

// Legacy GCS URL patterns
const GCS_URL_PATTERNS = [
  /https:\/\/storage\.googleapis\.com\/([^/]+)\/(.+)/,
  /https:\/\/([^.]+)\.storage\.googleapis\.com\/(.+)/,
];

// Profile image field names that should be processed
const PROFILE_IMAGE_FIELDS = new Set([
  'pfp_url', 'avatar_url', 'profile_image', 'profile_picture',
  'sender_pfp_url', 'photo_url', 'image_url'
]);

/**
 * Convert legacy GCS URLs to secure proxy URLs.
 * 
 * @param url - Original URL (legacy GCS URL or already converted proxy URL)
 * @returns Proxy URL for secure access through Stargate service
 * 
 * @example
 * ```typescript
 * const legacyUrl = "https://storage.googleapis.com/series-v1-profiles/profile.jpg";
 * const proxyUrl = convertLegacyGcsUrlToProxy(legacyUrl);
 * // Result: "/api/users/profile-image/series-v1-profiles/profile.jpg"
 * ```
 */
export function convertLegacyGcsUrlToProxy(url: string): string {
  if (!url || typeof url !== 'string') {
    return url || '';
  }

  const trimmedUrl = url.trim();
  if (!trimmedUrl) {
    return trimmedUrl;
  }

  // If it's already a proxy URL, return as-is
  if (trimmedUrl.startsWith('/api/users/profile-image/') || 
      trimmedUrl.startsWith('/api/storage/profile-image/')) {
    return trimmedUrl;
  }

  // Try to match legacy GCS URL patterns
  for (const pattern of GCS_URL_PATTERNS) {
    const match = trimmedUrl.match(pattern);
    if (match) {
      const [, bucketName, blobPath] = match;
      // Convert to proxy URL format (note: no /v1 prefix for this endpoint)
      const proxyUrl = `/api/users/profile-image/${bucketName}/${blobPath}`;
      
      console.debug('Converted legacy GCS URL to proxy URL:', {
        originalUrl: url,
        proxyUrl,
        bucket: bucketName,
        blobPath
      });
      
      return proxyUrl;
    }
  }

  // If it's not a GCS URL, return as-is
  return trimmedUrl;
}

/**
 * Convert relative proxy URL to absolute URL for image loading.
 * 
 * In development, routes through Vite proxy to Stargate.
 * In production, routes through Analytics service proxy to Stargate.
 * 
 * @param relativeUrl - Relative proxy URL
 * @returns Absolute URL for image loading
 */
export function getAbsoluteProxyUrl(relativeUrl: string): string {
  if (!relativeUrl || typeof relativeUrl !== 'string') {
    return relativeUrl || '';
  }

  const trimmedUrl = relativeUrl.trim();

  // If it's already absolute, return as-is
  if (trimmedUrl.startsWith('http')) {
    return trimmedUrl;
  }

  // If it's a proxy URL, handle routing properly
  if (trimmedUrl.startsWith('/api/users/profile-image/') || 
      trimmedUrl.startsWith('/api/storage/profile-image/')) {
    
    // In development: Use relative URL (Vite proxy will route to Stargate)
    // In production: Use relative URL (Analytics service will proxy to Stargate)
    // This ensures we always go through the proper proxy layer
    return trimmedUrl;
  }

  return trimmedUrl;
}

/**
 * Process a single profile image field value.
 * 
 * @param value - Field value (string URL, null, or other type)
 * @returns Processed value with URL conversion applied if applicable
 */
export function processProfileImageField(value: any): any {
  if (typeof value === 'string') {
    return convertLegacyGcsUrlToProxy(value);
  }
  return value;
}

/**
 * Process profile image URLs in an object recursively.
 * 
 * @param data - Object that may contain profile image URLs
 * @returns Object with converted URLs
 */
export function processProfileImageUrlsInObject(data: Record<string, any>): Record<string, any> {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return data;
  }

  const processedData: Record<string, any> = {};

  for (const [key, value] of Object.entries(data)) {
    if (PROFILE_IMAGE_FIELDS.has(key.toLowerCase())) {
      // Convert profile image URLs
      processedData[key] = processProfileImageField(value);
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      // Recursively process nested objects
      processedData[key] = processProfileImageUrlsInObject(value);
    } else if (Array.isArray(value)) {
      // Process arrays
      processedData[key] = processProfileImageUrlsInArray(value);
    } else {
      // Keep other values as-is
      processedData[key] = value;
    }
  }

  return processedData;
}

/**
 * Process profile image URLs in an array recursively.
 * 
 * @param data - Array that may contain objects with profile image URLs
 * @returns Array with converted URLs
 */
export function processProfileImageUrlsInArray(data: any[]): any[] {
  if (!Array.isArray(data)) {
    return data;
  }

  return data.map(item => {
    if (item && typeof item === 'object' && !Array.isArray(item)) {
      return processProfileImageUrlsInObject(item);
    } else if (Array.isArray(item)) {
      return processProfileImageUrlsInArray(item);
    } else {
      return item;
    }
  });
}

/**
 * Process response data to convert all profile image URLs.
 * 
 * This is the main function to use for processing API response data.
 * 
 * @param data - Response data (object, array, or other type)
 * @returns Processed data with converted URLs
 */
export function processResponseData(data: any): any {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return processProfileImageUrlsInObject(data);
  } else if (Array.isArray(data)) {
    return processProfileImageUrlsInArray(data);
  } else {
    return data;
  }
}

/**
 * Validate if a URL is a valid image URL.
 * 
 * @param url - URL to validate
 * @returns True if URL appears to be valid for images
 */
export function validateImageUrl(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }

  const trimmedUrl = url.trim();
  if (!trimmedUrl) {
    return false;
  }

  try {
    // Parse URL to check basic structure
    const parsed = new URL(trimmedUrl, window.location.origin);
    
    // Check for common image extensions (optional)
    const path = parsed.pathname.toLowerCase();
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
    
    // Allow URLs without extensions (they might be proxy URLs)
    return true; // Be permissive for now
    
  } catch (error) {
    console.warn(`URL validation failed for ${url}:`, error);
    return false;
  }
}

/**
 * Get a fallback image URL for when profile images fail to load.
 * 
 * @param name - User name for generating initials
 * @returns Data URL for a generated avatar or default image
 */
export function getFallbackImageUrl(name?: string): string {
  // For now, return empty string to let components handle fallbacks
  // In the future, could generate SVG avatars with initials
  return '';
}

/**
 * Process participant data to ensure profile images use proxy URLs.
 * 
 * @param participant - Participant object from API
 * @returns Participant with processed profile image URL
 */
export function processParticipant(participant: any): any {
  if (!participant || typeof participant !== 'object') {
    return participant;
  }

  return processProfileImageUrlsInObject(participant);
}

/**
 * Process conversation data to ensure all profile images use proxy URLs.
 * 
 * @param conversation - Conversation object from API
 * @returns Conversation with processed profile image URLs
 */
export function processConversation(conversation: any): any {
  if (!conversation || typeof conversation !== 'object') {
    return conversation;
  }

  return processProfileImageUrlsInObject(conversation);
}

/**
 * Process message data to ensure sender profile images use proxy URLs.
 * 
 * @param message - Message object from API
 * @returns Message with processed profile image URLs
 */
export function processMessage(message: any): any {
  if (!message || typeof message !== 'object') {
    return message;
  }

  return processProfileImageUrlsInObject(message);
}

// Export all conversion functions for use in components
export {
  STARGATE_SERVICE_URL,
  ANALYTICS_SERVICE_URL,
  PROFILE_IMAGE_FIELDS
};

// Example usage and testing (for development)
if (import.meta.env.DEV) {
  // Test URL conversion
  const testUrls = [
    'https://storage.googleapis.com/series-v1-profiles/profile-wybmCHqnz4pB.jpeg',
    'https://storage.googleapis.com/series-v1-profiles/pending_1757710344/20250912_205224_rwuFPo_image.jpg',
    '/api/users/profile-image/series-v1-profiles/already-converted.jpg',
    'https://external-site.com/image.jpg',
    null,
    '',
  ];

  console.group('Profile Image URL Conversion Tests:');
  testUrls.forEach(url => {
    const converted = convertLegacyGcsUrlToProxy(url as string);
    console.log(`  ${url} -> ${converted}`);
  });
  console.groupEnd();

  // Test data processing
  const testData = {
    user: {
      id: 123,
      name: 'Test User',
      pfp_url: 'https://storage.googleapis.com/series-v1-profiles/test.jpg',
      metadata: {
        avatar_url: 'https://storage.googleapis.com/series-v1-profiles/avatar.jpg'
      }
    },
    participants: [
      {
        id: 1,
        name: 'User 1',
        profile_image: 'https://storage.googleapis.com/series-v1-profiles/user1.jpg'
      },
      {
        id: 2,
        name: 'User 2',
        pfp_url: '/api/users/profile-image/series-v1-profiles/user2.jpg'
      }
    ]
  };

  console.group('Data Processing Test:');
  const processed = processResponseData(testData);
  console.log('Original:', testData);
  console.log('Processed:', processed);
  console.groupEnd();
}
